import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import LottieAnimationsLoader from 'Components/lottieAnimations/LottieAnimationsLoader';
import SFilterBox from 'Components/structural/SFilterBox/SFilterBox';
import { SFooter } from 'Components/structural/SFooter/SFooter';
import UButton from 'Components/unit/UButton/UButton';
import UTextLink from 'Components/unit/UTextLink/UTextLink';
import { useAppDispatch } from 'Libs/redux/store';
import { useTypedSelector } from 'Libs/redux/utils';
import { enqueueBasicAlert } from 'Pages/AlertManager/redux/utils';
import { selectAll } from 'Store/entities/activity/activity.selectors';
import { searchActivities } from 'Store/entities/activity/activity.thunks';

import styles from './ActivityList.style';
import { EmptyList } from './components/EmptyList/EmptyList';
import { CreateActivityModal } from './components/forms/CreateActivityModal';
import Item from './components/Item/Item';
import ItemSkeleton from './components/ItemSkeleton/ItemSkeleton';
import { selectedFiltersToQuery, useActivityFilters } from './hooks/useActivityFilter';

const ACTIVITY_LIST_LOADING_ITEMS = 25;
const ALERT_DURATION = 10000;

export const ActivityList: React.FC = () => {
  const dispatch = useAppDispatch();

  const [offset, setOffset] = useState(0);
  const [displayActivityCreationModal, setDisplayActivityCreationModal] = useState(false);

  const activities = useTypedSelector((state) => selectAll(state.entities.activity));
  const { loading: fetchingActivities, total, error } = useTypedSelector((state) => state.entities.activity.search);

  const { description, filters, onFilterChange, selectedFilters } = useActivityFilters({
    activityCount: activities.length,
  });

  const liveActivities = useMemo(() => activities.filter((activity) => activity.status === 'live'), [activities]);
  const upcomingActivities = useMemo(
    () => activities.filter((activity) => activity.status === 'upcoming'),
    [activities],
  );
  const otherActivities = useMemo(
    () => activities.filter((activity) => activity.status !== 'live' && activity.status !== 'upcoming'),
    [activities],
  );

  const handleFilterChange = useCallback(
    (selectedFilters) => {
      setOffset(0);
      onFilterChange(selectedFilters);
    },
    [onFilterChange],
  );

  useEffect(() => {
    if (error && error === 'search-error') {
      enqueueBasicAlert(
        {
          id: 'activity-list-error',
          title: t('activities:list.error.title'),
          text: t('activities:list.error.text'),
          status: 'error',
          icon: 'alert',
        },
        ALERT_DURATION,
      );
    }
  }, [error]);

  useEffect(() => {
    const query = selectedFiltersToQuery(selectedFilters);

    dispatch(searchActivities({ offset, limit: ACTIVITY_LIST_LOADING_ITEMS, query }));
  }, [offset, selectedFilters, dispatch]);

  const activityCreationModalToggle = () => {
    setDisplayActivityCreationModal((prev) => !prev);
  };

  return (
    <div style={styles.container}>
      <SFilterBox
        title={t('activities:list.filter.title')}
        placeholder={t('activities:list.filter.placeholder')}
        count={total ? total : null}
        description={description}
        filters={filters}
        onChange={handleFilterChange}
      />
      <div style={styles.list}>
        <UButton
          style={styles.createActivityButton}
          text={t('activities:list.create_activity')}
          type="accentuated"
          onClick={activityCreationModalToggle}
          trackingAction="create"
          trackingContext="activity"
        />
        {liveActivities.length > 0 && (
          <div style={{ ...styles.groupedActivitiesCard, ...styles.liveGroupedActivitiesCard }}>
            <div style={styles.groupedActivitiesCardHeader}>
              <LottieAnimationsLoader fileName="dotPulseGreen" height={40} width={40} />
              <span style={{ ...styles.groupedActivitiesCardTitle, ...styles.liveTitleColor }}>
                {t('activities:list.live_activities')}
              </span>
            </div>
            <div style={styles.groupedActivitiesCardBody}>
              {liveActivities.map((activity) => {
                return <Item key={activity.id} activityId={activity.id} />;
              })}
            </div>
          </div>
        )}

        {upcomingActivities.length > 0 && (
          <div style={{ ...styles.groupedActivitiesCard, ...styles.upcomingGroupedActivitiesCard }}>
            <div style={styles.groupedActivitiesCardHeader}>
              <LottieAnimationsLoader fileName="dotPulseBlue" height={40} width={40} />
              <span style={{ ...styles.groupedActivitiesCardTitle, ...styles.upcomingTitleColor }}>
                {t('activities:list.upcoming_activities')}
              </span>
            </div>
            <div style={styles.groupedActivitiesCardBody}>
              {upcomingActivities.map((activity) => {
                return <Item key={activity.id} activityId={activity.id} />;
              })}
            </div>
          </div>
        )}

        {(liveActivities.length > 0 || upcomingActivities.length > 0) && (
          <div style={styles.otherActivitiesSeparatorContainer}>
            <span style={styles.groupedActivitiesCardTitle}>{t('activities:list.other_activities')}</span>
            <div style={styles.separator} />
          </div>
        )}

        <div style={styles.otherActivitiesContainer}>
          {activities.length === 0 && !fetchingActivities && <EmptyList />}
          {otherActivities.map((activity) => {
            return <Item key={activity.id} activityId={activity.id} />;
          })}
          {fetchingActivities &&
            new Array(ACTIVITY_LIST_LOADING_ITEMS).fill(0).map((_, index) => <ItemSkeleton key={index} />)}
        </div>

        {offset + ACTIVITY_LIST_LOADING_ITEMS < total && (
          <SFooter>
            <UTextLink
              text={t('activities:list.load_more')}
              onClick={() => {
                setOffset((prevOffset) => prevOffset + ACTIVITY_LIST_LOADING_ITEMS);
              }}
              type="secondary"
              rightIcon="bottom-chevron"
            />
          </SFooter>
        )}
      </div>
      <CreateActivityModal visible={displayActivityCreationModal} onClose={activityCreationModalToggle} />
    </div>
  );
};

export default ActivityList;
